import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';

import { detectSurface } from './lib/subdomain.js';
import { AdminAiUsagePage } from './routes/admin/ai-usage.js';
import { AdminAuditPage } from './routes/admin/audit.js';
import { AdminLoginPage } from './routes/admin/auth/admin-login.js';
import { AdminTotpSetupPage } from './routes/admin/auth/admin-totp-setup.js';
import { AdminDashboardPage } from './routes/admin/dashboard.js';
import { AdminLayout } from './routes/admin/layout.js';
import { AdminOrgDetailPage } from './routes/admin/org-detail.js';
import { AdminOrgsPage } from './routes/admin/orgs.js';
import { AcceptInvitationPage } from './routes/auth/accept-invitation.js';
import { ForgotPasswordPage } from './routes/auth/forgot-password.js';
import { HandoffPage } from './routes/auth/handoff.js';
import { LoginPage } from './routes/auth/login.js';
import { ResetPasswordPage } from './routes/auth/reset-password.js';
import { PublicUnsubscribePage } from './routes/public/unsubscribe.js';
import { VendorCallbackPage } from './routes/auth/vendor-callback.js';
import { VendorRequestPage } from './routes/auth/vendor-request.js';
import { ReviewQueuePage } from './routes/org/ai/review-queue.js';
import { BuildingDetailPage } from './routes/org/buildings/building-detail.js';
import { BuildingFormPage } from './routes/org/buildings/building-form.js';
import { BuildingsListPage } from './routes/org/buildings/buildings-list.js';
import { ImportWizardPage } from './routes/org/buildings/import-wizard.js';
import { ComplianceDashboardPage } from './routes/org/compliance/dashboard.js';
import { ComplianceDeviceTypesPage } from './routes/org/compliance/device-types.js';
import { ComplianceDevicesPage } from './routes/org/compliance/devices.js';
import { ComplianceIngestPage } from './routes/org/compliance/ingest.js';
import { ComplianceLayout } from './routes/org/compliance/layout.js';
import { ComplianceRecordDetailPage } from './routes/org/compliance/record-detail.js';
import { ComplianceRecordsPage } from './routes/org/compliance/records.js';
import { DashboardPage } from './routes/org/dashboard.js';
import { InspectionsDashboardPage } from './routes/org/inspections/dashboard.js';
import { InspectionDetailPage } from './routes/org/inspections/inspection-detail.js';
import { InspectionsListPage } from './routes/org/inspections/inspections-list.js';
import { InspectionsLayout } from './routes/org/inspections/layout.js';
import { InspectionSchedulesPage } from './routes/org/inspections/schedules-list.js';
import { InspectionTemplateDetailPage } from './routes/org/inspections/template-detail.js';
import { InspectionTemplatesPage } from './routes/org/inspections/templates-list.js';
import { OrgLayout } from './routes/org/layout.js';
import { LeadRollupPage } from './routes/org/lead/rollup.js';
import { RemindersListPage } from './routes/org/reminders/list.js';
import { OrgUsersPage } from './routes/org/settings/admin-users.js';
import { AiSettingsPage } from './routes/org/settings/ai-settings.js';
import { InboxPage } from './routes/org/inbox.js';
import { SyncConflictsPage } from './routes/org/sync/conflicts.js';
import { OrgBrandingPage } from './routes/org/settings/branding.js';
import { DisplaySettingsPage } from './routes/org/settings/display-settings.js';
import { NotificationsSettingsPage } from './routes/org/settings/notifications.js';
import { ProfileSettingsPage } from './routes/org/settings/profile.js';
import { SecuritySettingsPage } from './routes/org/settings/security.js';
import { SettingsLayout } from './routes/org/settings/settings-layout.js';
import { TeamsSettingsPage } from './routes/org/settings/teams.js';
import { UnitFieldsSettingsPage } from './routes/org/settings/unit-fields.js';
import { VendorCompanyDetailPage } from './routes/org/settings/vendor-detail.js';
import { VendorsSettingsPage } from './routes/org/settings/vendors.js';
import { ViolationsSettingsPage } from './routes/org/settings/violations-settings.js';
import { WorkOrdersSettingsPage } from './routes/org/settings/work-orders-settings.js';
import { UnitDetailPage } from './routes/org/units/unit-detail.js';
import { ViolationAgencyPage } from './routes/org/violations/agency.js';
import { ViolationsBoardPage } from './routes/org/violations/board.js';
import { ViolationBuildingPage } from './routes/org/violations/building.js';
import { ViolationDetailPage } from './routes/org/violations/detail.js';
import { WorkOrderDetailPage } from './routes/org/work-orders/detail.js';
import { WorkOrdersListPage } from './routes/org/work-orders/list.js';
import { RootRoute } from './routes/root.js';
import { VendorLayout } from './routes/vendor/layout.js';
import { VendorLogsIndexPage } from './routes/vendor/logs-index.js';
import { VendorSignoffFlowPage } from './routes/vendor/signoff-flow.js';
import { VendorUnitLogsPage } from './routes/vendor/unit-logs.js';
import { VendorWorkDetailPage } from './routes/vendor/work-detail.js';
import { VendorWorkListPage } from './routes/vendor/work-list.js';
import { useAuthStore } from './stores/auth-store.js';

/**
 * Loaders that gate authed sections. We don't fetch in the loader (we
 * leave that to TanStack Query inside the page) — we only check whether
 * the right kind of token is present and short-circuit to the login page
 * if not.
 */
function requireOrgAuth() {
  const { accessToken, kind } = useAuthStore.getState();
  if (!accessToken || kind !== 'org') return redirect('/login');
  return null;
}

function requireVendorAuth() {
  const { accessToken, kind } = useAuthStore.getState();
  if (!accessToken || kind !== 'vendor') return redirect('/vendor');
  return null;
}

function requireAdminAuth() {
  const { accessToken, kind } = useAuthStore.getState();
  if (!accessToken || kind !== 'admin') return redirect('/admin/login');
  return null;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRoute />,
    children: [
      // ---- Org subdomain ----
      { index: true, element: <SurfaceIndex /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'accept-invitation', element: <AcceptInvitationPage /> },
      // Phase 9 — public one-click unsubscribe landing
      { path: 'u/:token', element: <PublicUnsubscribePage /> },
      { path: 'handoff', element: <HandoffPage /> },
      { path: 'vendor', element: <VendorRequestPage /> },
      { path: 'vendor/auth/callback', element: <VendorCallbackPage /> },
      {
        path: 'vendor',
        loader: requireVendorAuth,
        element: <VendorLayout />,
        children: [
          { path: 'work', element: <VendorWorkListPage /> },
          { path: 'work/:id', element: <VendorWorkDetailPage /> },
          { path: 'work/:id/signoff', element: <VendorSignoffFlowPage /> },
          { path: 'work/:id/units', element: <VendorUnitLogsPage /> },
          { path: 'logs', element: <VendorLogsIndexPage /> },
        ],
      },

      {
        path: '',
        loader: requireOrgAuth,
        element: <OrgLayout />,
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'buildings', element: <BuildingsListPage /> },
          { path: 'buildings/new', element: <BuildingFormPage mode="create" /> },
          { path: 'buildings/import', element: <ImportWizardPage /> },
          { path: 'buildings/import/:id', element: <ImportWizardPage /> },
          { path: 'buildings/:id', element: <BuildingDetailPage /> },
          { path: 'buildings/:id/edit', element: <BuildingFormPage mode="edit" /> },
          { path: 'units/:id', element: <UnitDetailPage /> },
          { path: 'work-orders', element: <WorkOrdersListPage /> },
          { path: 'work-orders/:id', element: <WorkOrderDetailPage /> },
          { path: 'ai/review-queue', element: <ReviewQueuePage /> },
          {
            path: 'compliance',
            element: <ComplianceLayout />,
            children: [
              { index: true, element: <ComplianceDashboardPage /> },
              { path: 'records', element: <ComplianceRecordsPage /> },
              { path: 'records/:id', element: <ComplianceRecordDetailPage /> },
              { path: 'devices', element: <ComplianceDevicesPage /> },
              { path: 'upload', element: <ComplianceIngestPage /> },
              { path: 'device-types', element: <ComplianceDeviceTypesPage /> },
            ],
          },
          { path: 'lead', element: <LeadRollupPage /> },
          { path: 'reminders', element: <RemindersListPage /> },
          { path: 'tasks', element: <RemindersListPage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'notifications', element: <Navigate to="/inbox" replace /> },
          { path: 'sync/conflicts', element: <SyncConflictsPage /> },
          { path: 'violations', element: <ViolationsBoardPage /> },
          { path: 'violations/agency/:agency', element: <ViolationAgencyPage /> },
          {
            path: 'violations/agency/:agency/buildings/:buildingId',
            element: <ViolationBuildingPage />,
          },
          { path: 'violations/:id', element: <ViolationDetailPage /> },
          {
            path: 'inspections',
            element: <InspectionsLayout />,
            children: [
              { index: true, element: <InspectionsDashboardPage /> },
              { path: 'runs', element: <InspectionsListPage /> },
              { path: 'runs/:id', element: <InspectionDetailPage /> },
              { path: 'templates', element: <InspectionTemplatesPage /> },
              { path: 'templates/:id', element: <InspectionTemplateDetailPage /> },
              { path: 'schedules', element: <InspectionSchedulesPage /> },
            ],
          },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              { path: 'profile', element: <ProfileSettingsPage /> },
              { path: 'security', element: <SecuritySettingsPage /> },
              { path: 'notifications', element: <NotificationsSettingsPage /> },
              { path: 'branding', element: <OrgBrandingPage /> },
              { path: 'users', element: <OrgUsersPage /> },
              { path: 'teams', element: <TeamsSettingsPage /> },
              { path: 'work-orders', element: <WorkOrdersSettingsPage /> },
              { path: 'unit-fields', element: <UnitFieldsSettingsPage /> },
              { path: 'vendors', element: <VendorsSettingsPage /> },
              { path: 'vendors/:id', element: <VendorCompanyDetailPage /> },
              { path: 'violations', element: <ViolationsSettingsPage /> },
              { path: 'ai', element: <AiSettingsPage /> },
              { path: 'display', element: <DisplaySettingsPage /> },
            ],
          },
        ],
      },

      // ---- Admin subdomain ----
      { path: 'admin/login', element: <AdminLoginPage /> },
      { path: 'admin/totp-setup', element: <AdminTotpSetupPage /> },
      {
        path: 'admin',
        loader: requireAdminAuth,
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminDashboardPage /> },
          { path: 'orgs', element: <AdminOrgsPage /> },
          { path: 'orgs/:id', element: <AdminOrgDetailPage /> },
          { path: 'ai', element: <AdminAiUsagePage /> },
          { path: 'audit', element: <AdminAuditPage /> },
        ],
      },
    ],
  },
]);

/** Sends visitors to the right landing route based on the active subdomain. */
function SurfaceIndex() {
  const surface = detectSurface();
  if (surface.kind === 'admin') {
    const { accessToken, kind } = useAuthStore.getState();
    if (accessToken && kind === 'admin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/admin/login" replace />;
  }
  if (surface.kind === 'org') {
    const { accessToken, kind } = useAuthStore.getState();
    if (accessToken && kind === 'org') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }
  return <Navigate to="/login" replace />;
}
